import { createFileRoute } from '@tanstack/react-router'
import { useState, useMemo } from 'react'
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, PageHeader } from '@/components/ui'
import { cn } from '@/lib/cn'
import { Truck, Package, MapPin, Calendar, Search, Settings2, CheckCircle2 } from 'lucide-react'

type ShippingType = 'domestic' | 'international'
type DomesticProvider = 'interrapidisimo' | 'servientrega' | 'coordinadora' | 'dhl'
type InternationalProvider = 'myshipping' | 'dhl'
type ShippingProvider = DomesticProvider | InternationalProvider
type GuideStatus = 'draft' | 'generated' | 'in_transit' | 'delivered' | 'cancelled'
type EventType = 'created' | 'manifested' | 'in_transit' | 'out_for_delivery' | 'delivered' | 'failed' | 'returned'

interface ShippingGuide {
  id: string
  guideNumber: string
  orderNumber: string
  tenantName: string
  tenantSlug: string
  type: ShippingType
  provider: ShippingProvider
  recipient: {
    name: string
    phone: string
    email: string
    address: string
    city: string
    country?: string
    zip: string
  }
  weight: number
  value: number
  status: GuideStatus
  trackingUrl: string
  createdAt: string
  events: ShippingEvent[]
}

interface ShippingEvent {
  id: string
  type: EventType
  timestamp: string
  location: string
  description: string
}

const DOMESTIC_PROVIDERS = [
  { id: 'interrapidisimo' as const, name: 'Interrapidísimo', logo: '📦', isConfigured: true },
  { id: 'servientrega' as const, name: 'Servientrega', logo: '🚚', isConfigured: false },
  { id: 'coordinadora' as const, name: 'Coordinadora', logo: '🏢', isConfigured: false },
  { id: 'dhl' as const, name: 'DHL', logo: '✈️', isConfigured: false },
]

const INTERNATIONAL_PROVIDERS = [
  { id: 'myshipping' as const, name: 'MyShipping', logo: '🌍', isConfigured: true },
  { id: 'dhl' as const, name: 'DHL Express', logo: '✈️', isConfigured: true },
]

const SEED_GUIDES: ShippingGuide[] = [
  // Envíos Nacionales
  {
    id: 'guide-001',
    guideNumber: 'INTER-2026-05-001',
    orderNumber: '#ORD-2026-001',
    tenantName: 'Centrio Beauty',
    tenantSlug: 'centrio-beauty',
    type: 'domestic',
    provider: 'interrapidisimo',
    recipient: { name: 'Juan López', phone: '+57 301 234 5678', email: 'juan@example.com', address: 'Cra 7 #45-89', city: 'Bogotá', zip: '110221' },
    weight: 2.5,
    value: 85000,
    status: 'delivered',
    trackingUrl: 'https://tracking.interrapidisimo.com/INTER-2026-05-001',
    createdAt: '2026-05-01T10:30:00Z',
    events: [
      { id: 'ev-1', type: 'created', timestamp: '2026-05-01T10:30:00Z', location: 'Bogotá', description: 'Guía creada' },
      { id: 'ev-2', type: 'manifested', timestamp: '2026-05-01T15:45:00Z', location: 'Centro de Dist.', description: 'Recibido' },
      { id: 'ev-5', type: 'delivered', timestamp: '2026-05-06T14:20:00Z', location: 'Cra 7', description: 'Entregado' },
    ],
  },
  {
    id: 'guide-002',
    guideNumber: 'INTER-2026-05-002',
    orderNumber: '#ORD-2026-002',
    tenantName: 'Urban Fit',
    tenantSlug: 'urban-fit',
    type: 'domestic',
    provider: 'interrapidisimo',
    recipient: { name: 'María García', phone: '+57 310 987 6543', email: 'maria@example.com', address: 'Av Paseo', city: 'Medellín', zip: '050001' },
    weight: 1.2,
    value: 65000,
    status: 'in_transit',
    trackingUrl: 'https://tracking.interrapidisimo.com/INTER-2026-05-002',
    createdAt: '2026-05-05T14:00:00Z',
    events: [
      { id: 'ev-1', type: 'created', timestamp: '2026-05-05T14:00:00Z', location: 'Bogotá', description: 'Guía creada' },
      { id: 'ev-3', type: 'in_transit', timestamp: '2026-05-06T09:00:00Z', location: 'Ruta Medellín', description: 'En tránsito' },
    ],
  },
  {
    id: 'guide-003',
    guideNumber: 'INTER-2026-05-003',
    orderNumber: '#ORD-2026-003',
    tenantName: 'North Home',
    tenantSlug: 'north-home',
    type: 'domestic',
    provider: 'interrapidisimo',
    recipient: { name: 'Carlos Rodríguez', phone: '+57 315 456 7890', email: 'carlos@example.com', address: 'Calle 10', city: 'Barranquilla', zip: '080001' },
    weight: 0.8,
    value: 45000,
    status: 'generated',
    trackingUrl: 'https://tracking.interrapidisimo.com/INTER-2026-05-003',
    createdAt: '2026-05-07T11:00:00Z',
    events: [
      { id: 'ev-1', type: 'created', timestamp: '2026-05-07T11:00:00Z', location: 'Bogotá', description: 'Guía creada' },
    ],
  },
  // Envíos Internacionales
  {
    id: 'guide-004',
    guideNumber: 'MYS-2026-05-001',
    orderNumber: '#ORD-2026-004',
    tenantName: 'Centrio Beauty',
    tenantSlug: 'centrio-beauty',
    type: 'international',
    provider: 'myshipping',
    recipient: { name: 'Sarah Johnson', phone: '+1 212 555 1234', email: 'sarah@example.com', address: '123 Madison Ave', city: 'New York', country: 'USA', zip: '10001' },
    weight: 3.2,
    value: 250000,
    status: 'in_transit',
    trackingUrl: 'https://myshipping.com/track/MYS-2026-05-001',
    createdAt: '2026-05-03T08:00:00Z',
    events: [
      { id: 'ev-1', type: 'created', timestamp: '2026-05-03T08:00:00Z', location: 'Bogotá', description: 'Guía creada' },
      { id: 'ev-2', type: 'manifested', timestamp: '2026-05-04T12:00:00Z', location: 'Centro Aéreo', description: 'En aduana' },
      { id: 'ev-3', type: 'in_transit', timestamp: '2026-05-06T15:30:00Z', location: 'Miami Hub', description: 'En transporte' },
    ],
  },
  {
    id: 'guide-005',
    guideNumber: 'DHL-2026-05-001',
    orderNumber: '#ORD-2026-005',
    tenantName: 'Urban Fit',
    tenantSlug: 'urban-fit',
    type: 'international',
    provider: 'dhl',
    recipient: { name: 'Anna Mueller', phone: '+49 30 555 5678', email: 'anna@example.com', address: 'Kurfürstendamm 123', city: 'Berlin', country: 'Germany', zip: '10711' },
    weight: 2.0,
    value: 180000,
    status: 'delivered',
    trackingUrl: 'https://dhl.de/tracking/DHL-2026-05-001',
    createdAt: '2026-05-01T14:00:00Z',
    events: [
      { id: 'ev-1', type: 'created', timestamp: '2026-05-01T14:00:00Z', location: 'Bogotá', description: 'Guía creada' },
      { id: 'ev-2', type: 'manifested', timestamp: '2026-05-02T10:00:00Z', location: 'Centro de Exp.', description: 'Manifestado' },
      { id: 'ev-3', type: 'in_transit', timestamp: '2026-05-04T09:00:00Z', location: 'Frankfurt', description: 'Centro de dist.' },
      { id: 'ev-4', type: 'out_for_delivery', timestamp: '2026-05-07T08:30:00Z', location: 'Berlin', description: 'En reparto' },
      { id: 'ev-5', type: 'delivered', timestamp: '2026-05-07T16:45:00Z', location: 'Kurfürstendamm', description: 'Entregado' },
    ],
  },
]

function LogisticsPage() {
  // Refresco intencional para forzar regeneración del árbol de rutas.
  const [guides] = useState(SEED_GUIDES)
  const [shippingType, setShippingType] = useState<ShippingType>('domestic')
  const [selectedGuide, setSelectedGuide] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | GuideStatus>('all')
  const [tenantFilter, setTenantFilter] = useState<'all' | string>('all')

  const selected = guides.find((g) => g.id === selectedGuide)
  const tenants = Array.from(new Set(guides.map((g) => g.tenantSlug)))

  const stats = useMemo(() => ({
    total: guides.length,
    generated: guides.filter((g) => g.status === 'generated').length,
    inTransit: guides.filter((g) => g.status === 'in_transit').length,
    delivered: guides.filter((g) => g.status === 'delivered').length,
  }), [guides])

  const filtered = useMemo(() => {
    let list = guides.filter((g) => g.type === shippingType)
    if (statusFilter !== 'all') list = list.filter((g) => g.status === statusFilter)
    if (tenantFilter !== 'all') list = list.filter((g) => g.tenantSlug === tenantFilter)
    if (search) {
      const q = search.toLowerCase()
      list = list.filter((g) => g.guideNumber.toLowerCase().includes(q) || g.recipient.name.toLowerCase().includes(q))
    }
    return list
  }, [guides, shippingType, statusFilter, tenantFilter, search])

  // Set first guide when shipping type changes
  useMemo(() => {
    const firstGuide = filtered[0]?.id ?? null
    setSelectedGuide(firstGuide)
  }, [shippingType, filtered])

  return (
    <>
      <PageHeader title="Logística" description="Gestión de envíos y operadores nacionales e internacionales" />
      <div className="border-b border-border">
        <div className="px-8 pt-6 flex gap-4">
          {(['domestic', 'international'] as const).map((type) => (
            <button
              key={type}
              onClick={() => setShippingType(type)}
              className={cn('pb-3 px-1 text-sm font-medium transition border-b-2', 
                shippingType === type ? 'border-cyan-500 text-cyan-700 dark:text-cyan-400' : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              {type === 'domestic' ? '📦 Nacionales' : '🌍 Internacionales'}
            </button>
          ))}
        </div>
      </div>
      <div className="p-8 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Total" value={stats.total} icon={Truck} color="text-blue-700 dark:text-blue-400" />
          <StatCard label="Generadas" value={stats.generated} icon={Package} color="text-yellow-700 dark:text-yellow-400" />
          <StatCard label="Tránsito" value={stats.inTransit} icon={MapPin} color="text-[hsl(var(--primary))]" />
          <StatCard label="Entregadas" value={stats.delivered} icon={CheckCircle2} color="text-emerald-700 dark:text-emerald-400" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sidebar */}
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input type="search" placeholder="Buscar..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>

            <div>
              <p className="text-xs text-muted-foreground mb-2">ESTADO</p>
              <div className="flex flex-wrap gap-2">
                {(['all', 'generated', 'in_transit', 'delivered'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    className={cn('px-3 py-1 text-xs rounded-md border transition', statusFilter === s ? 'bg-[hsl(var(--muted))] text-foreground' : 'bg-[hsl(var(--surface))] text-muted-foreground')}
                  >
                    {s === 'all' ? 'Todas' : s === 'generated' ? 'Generadas' : s === 'in_transit' ? 'Tránsito' : 'Entregadas'}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs text-muted-foreground mb-2">TIENDA</p>
              <select
                value={tenantFilter}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTenantFilter(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-border bg-[hsl(var(--surface))] text-foreground text-sm"
              >
                <option value="all">Todas</option>
                {tenants.map((t) => (
                  <option key={t} value={t}>
                    {SEED_GUIDES.find((g) => g.tenantSlug === t)?.tenantName}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filtered.map((g) => (
                <button
                  key={g.id}
                  onClick={() => setSelectedGuide(g.id)}
                  className={cn('w-full rounded-lg border p-3 text-left text-sm transition', selectedGuide === g.id ? 'border-cyan-200 dark:border-cyan-700 bg-cyan-50 dark:bg-cyan-950/30' : 'border-border bg-background/40')}
                >
                  <p className="font-mono text-cyan-700 dark:text-cyan-400">{g.guideNumber}</p>
                  <p className="text-xs text-foreground">{g.recipient.name}</p>
                  <p className="text-xs text-muted-foreground">{g.tenantName}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Detail Panel */}
          {selected && (
            <div className="lg:col-span-2 space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-start gap-3">
                    <div>
                      <CardTitle className="text-lg">{selected.guideNumber}</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">{selected.orderNumber}</p>
                    </div>
                    <Badge>{(shippingType === 'domestic' ? DOMESTIC_PROVIDERS : INTERNATIONAL_PROVIDERS).find((p) => p.id === selected.provider)?.name}</Badge>
                  </div>
                  <div className="space-y-2 text-sm mt-3">
                    <p className="flex gap-2">
                      <Package className="size-4" />
                      {selected.weight} kg • ${selected.value.toLocaleString()}
                    </p>
                    <p className="flex gap-2">
                      <MapPin className="size-4" />
                      {selected.recipient.city}
                    </p>
                    <p className="flex gap-2">
                      <Calendar className="size-4" />
                      {new Date(selected.createdAt).toLocaleDateString('es-CO')}
                    </p>
                  </div>
                </CardHeader>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Rastreo</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {selected.events.map((e, i) => (
                    <div key={e.id} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className={cn('size-3 rounded-full border-2', i === selected.events.length - 1 ? 'border-cyan-500 bg-cyan-500' : 'border-border bg-[hsl(var(--muted))]')} />
                        {i < selected.events.length - 1 && <div className="w-0.5 h-6 bg-[hsl(var(--muted))] mt-1" />}
                      </div>
                      <div className="text-xs">
                        <p className="font-medium text-foreground">{e.description}</p>
                        <p className="text-muted-foreground">{e.location}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Destinatario</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 text-sm">
                  <p>
                    <span className="text-muted-foreground">Nombre:</span> {selected.recipient.name}
                  </p>
                  <p>
                    <span className="text-muted-foreground">Teléfono:</span> {selected.recipient.phone}
                  </p>
                  <p>
                    <span className="text-muted-foreground">Email:</span> {selected.recipient.email}
                  </p>
                  <p>
                    <span className="text-muted-foreground">Dirección:</span> {selected.recipient.address}
                  </p>
                  {selected.recipient.country && (
                    <p>
                      <span className="text-muted-foreground">País:</span> {selected.recipient.country}
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex gap-2">
              <Settings2 className="size-4" /> Operadores {shippingType === 'domestic' ? 'Nacionales' : 'Internacionales'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {(shippingType === 'domestic' ? DOMESTIC_PROVIDERS : INTERNATIONAL_PROVIDERS).map((p) => (
                <div key={p.id} className={cn('rounded-lg border p-3', p.isConfigured ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/20' : 'border-border bg-background/40')}>
                  <p className="text-xl mb-2">{p.logo}</p>
                  <p className="text-xs font-medium text-foreground">{p.name}</p>
                  <Button variant="ghost" size="sm" className="w-full mt-2 text-xs">
                    {p.isConfigured ? '✓' : 'Conf'}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  )
}

function StatCard({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string
  value: number
  icon: any
  color: string
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
          </div>
          <Icon className={cn('size-6', color)} />
        </div>
      </CardContent>
    </Card>
  )
}

export const Route = createFileRoute('/store/$id/logistics')({
  component: LogisticsPage,
})
